import { createContext, useContext, useEffect, useState } from "react";
import { useConfig } from "../../config/ConfigContext";
import { getToys } from "../../utils/getToys";
import { getToysAvailability } from "../../utils/getToysAvailability";

const ToysContext = createContext();

export const ToysProvider = ({ children }) => {
  const config = useConfig();
  const [toys, setToys] = useState([]);
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reservationDate, setReservationDate] = useState(null);
  const [availabilityByToyId, setAvailabilityByToyId] = useState({});
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  useEffect(() => {
    if (!config) return;
    const fetchToys = async () => {
      setIsLoading(true);
      try {
        const toys = await getToys(config);
        setToys(toys);
      } finally {
        setIsLoading(false);
      }
    };
    fetchToys();
  }, [config]);

  useEffect(() => {
    if (!reservationDate || !toys.length) {
      setAvailabilityByToyId({});
      return;
    }
    let cancelled = false;
    const fetchAvailability = async () => {
      setIsLoadingAvailability(true);
      try {
        const reservedByToyId = await getToysAvailability(reservationDate);
        if (cancelled) return;
        const available = {};
        for (const toy of toys) {
          const maxStock = toy.maxStock ?? 0;
          const reserved = reservedByToyId[toy.id] ?? 0;
          available[toy.id] = Math.max(0, maxStock - reserved);
        }
        setAvailabilityByToyId(available);
      } finally {
        if (!cancelled) {
          setIsLoadingAvailability(false);
        }
      }
    };
    fetchAvailability();
    return () => {
      cancelled = true;
    };
  }, [reservationDate, toys]);

  return (
    <ToysContext.Provider
      value={{
        toys,
        setToys,
        cart,
        setCart,
        isLoading,
        setIsLoading,
        reservationDate,
        setReservationDate,
        availabilityByToyId,
        isLoadingAvailability,
      }}
    >
      {children}
    </ToysContext.Provider>
  );
};

export const useToys = () => useContext(ToysContext);
